import { ArrowRight } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "../components/Reveal";
import { CountUp } from "../components/CountUp";
import { useEnquiry } from "../context/EnquiryContext";
import {
  ABOUT_COPY_1,
  ABOUT_COPY_2,
  ABOUT_COPY_MANUFACTURER,
  IMAGES,
  LEADERSHIP,
  MISSION,
  VISION,
  WHY_POINTS,
} from "../data/content";

export default function About() {
  const { openEnquiry } = useEnquiry();

  return (
    <main data-testid="about-page">
      <section className="pt-32 sm:pt-44 pb-16 sm:pb-24 px-5 sm:px-8 lg:px-12 border-b border-ink/15">
        <FadeUp immediate y={12}>
          <Overline className="mb-8">About Jagdamba Enterprises</Overline>
        </FadeUp>
        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-[13vw] sm:text-8xl">
          <MaskedLine immediate delay={0.15}>Built on Trust.</MaskedLine>
          <MaskedLine immediate delay={0.3}>Delivered with</MaskedLine>
          <MaskedLine immediate delay={0.45}>Precision.</MaskedLine>
        </h1>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 border-b border-ink/15">
        <div className="lg:col-span-7 px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <FadeUp>
            <Overline className="mb-8">Who We Are</Overline>
            <div className="space-y-6 max-w-2xl">
              <p className="text-lg sm:text-xl text-ink/80 leading-relaxed">{ABOUT_COPY_1}</p>
              <p className="text-lg sm:text-xl text-ink/80 leading-relaxed border-l-2 border-accent pl-5">
                {ABOUT_COPY_MANUFACTURER}
              </p>
              <p className="text-base sm:text-lg text-ink/65 leading-relaxed">{ABOUT_COPY_2}</p>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-12 gap-y-8">
              <div>
                <p className="font-display font-black text-5xl sm:text-6xl tracking-tighter">
                  <CountUp to={25} suffix="+" />
                </p>
                <p className="mt-2 font-mono text-[11px] tracking-[0.25em] uppercase text-ink/50">
                  Years of Experience
                </p>
              </div>
              <div>
                <p className="font-display font-black text-5xl sm:text-6xl tracking-tighter">
                  <CountUp to={50} suffix="+" />
                </p>
                <p className="mt-2 font-mono text-[11px] tracking-[0.25em] uppercase text-ink/50">
                  Trusted Brands
                </p>
              </div>
              <div>
                <p className="font-display font-black text-5xl sm:text-6xl tracking-tighter">
                  <CountUp to={100} suffix="K+" />
                </p>
                <p className="mt-2 font-mono text-[11px] tracking-[0.25em] uppercase text-ink/50">
                  Satisfied Customers
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-5 border-t lg:border-t-0 lg:border-l border-ink/15">
          <div className="h-full min-h-[360px] overflow-hidden">
            <img
              src={IMAGES.tools}
              alt="Industrial tools arranged on a dark surface"
              className="h-full w-full object-cover grayscale contrast-125"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 border-b border-ink/15">
        <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 border-b lg:border-b-0 lg:border-r border-ink/15">
          <FadeUp>
            <Overline className="mb-8">Our Vision</Overline>
            <p className="font-display font-bold tracking-tight leading-[1.15] text-2xl sm:text-4xl">
              {VISION}
            </p>
          </FadeUp>
        </div>
        <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <FadeUp delay={0.1}>
            <Overline className="mb-8">Our Mission</Overline>
            <p className="font-display font-bold tracking-tight leading-[1.15] text-2xl sm:text-4xl">
              {MISSION}
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32 border-b border-ink/15 bg-white">
        <FadeUp>
          <Overline className="mb-6">Leadership</Overline>
        </FadeUp>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl mb-14">
          <MaskedLine>The People Behind</MaskedLine>
          <MaskedLine delay={0.12}>Jagdamba</MaskedLine>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/15 border border-ink/15">
          {LEADERSHIP.map((p, i) => (
            <div key={p.name} className="bg-white" data-testid={`about-leader-${p.name.toLowerCase().replace(" ", "-")}`}>
              <FadeUp delay={i * 0.1}>
                <div className="aspect-[4/3] overflow-hidden border-b border-ink/15">
                  <img
                    src={p.photo}
                    alt={`${p.name}, ${p.role} of Jagdamba Enterprises`}
                    className="h-full w-full object-cover object-top grayscale contrast-105 hover:grayscale-0 hover:scale-[1.02] transition-all duration-[1.2s] ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="px-8 sm:px-12 py-12 sm:py-16">
                  <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink/45 mb-6">
                    {String(i + 1).padStart(2, "0")} — {p.role}
                  </p>
                  <h3 className="font-display font-black uppercase tracking-tighter leading-none text-4xl sm:text-6xl">
                    {p.name}
                  </h3>
                  <div className="mt-8 h-1 w-14 bg-accent" />
                </div>
              </FadeUp>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 border-b border-ink/15">
        <FadeUp>
          <Overline className="mb-6">Why Jagdamba?</Overline>
        </FadeUp>
        <div className="border-t border-ink/15">
          {WHY_POINTS.map((point, i) => (
            <FadeUp key={point} delay={i * 0.04}>
              <div className="flex items-baseline gap-6 sm:gap-12 py-6 border-b border-ink/15">
                <span className="font-mono text-xs text-ink/40">0{i + 1}</span>
                <h3 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl">
                  {point}
                </h3>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-12">
          <button
            data-testid="about-cta-send-requirement"
            onClick={() => openEnquiry("")}
            className="inline-flex items-center gap-2 bg-ink text-paper font-mono text-xs tracking-[0.2em] uppercase px-8 py-5 hover:bg-accent hover:text-ink transition-colors"
          >
            Send Your Requirement <ArrowRight size={14} />
          </button>
        </FadeUp>
      </section>
    </main>
  );
}
